import React, { useCallback } from 'react';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  DEFAULT_DATE_FORMAT,
  DateFormat,
  format,
} from '@onevision-resources/date-utils';
import {
  DEFAULT_INPUT_DATE_MASK,
  DEFAULT_PICKER_VIEWS,
} from 'constants/dates';

/** True when the text field has no typed digits (empty, cleared, or mask placeholders only). */
const isInputClearedOrMaskOnly = (raw: string | undefined | null) =>
  raw == null || raw === '' || !/\d/.test(raw);

interface Props {
  name: string;
  value: Date | string | null;
  error?: string;
  label: string;
  className?: string;
  minDate?: string;
  maxDate?: string;
  defaultCalendarMonth?: string;
  disablePast?: boolean;
  inputFormat?: string;
  mask?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  onChange?: (value: Date | null, inputValue?: string) => void;
  onBlur?: (event: unknown) => void;
  setFieldValue?: (value: Date | string | null) => void;
}

/** This component is created as a fix and based on day-zero DzDateTimePicker */
export const DatePickerInput: React.FC<Props> = ({
  name,
  value,
  error,
  inputFormat = DEFAULT_DATE_FORMAT,
  mask = DEFAULT_INPUT_DATE_MASK,
  setFieldValue,
  label,
  className,
  minDate,
  maxDate,
  defaultCalendarMonth,
  disablePast,
  disabled,
  fullWidth,
  onChange,
  onBlur,
}) => {
  const pickerValue =
    value != null && value !== ''
      ? (() => {
          const parsed = dayjs(value);
          return parsed.isValid() ? parsed : null;
        })()
      : null;

  const onDateChange = useCallback(
    (date: Date | null, inputValue?: string) => {
      if (onChange) {
        return onChange(date, inputValue);
      }

      // MUI passes null for both "cleared" and invalid/partial keyboard input; only null the
      // form when the input is actually empty or mask-only, otherwise keep the string for Yup.
      if (date == null) {
        setFieldValue?.(
          isInputClearedOrMaskOnly(inputValue) ? null : inputValue ?? null,
        );
        return;
      }

      try {
        const sanitizedValue =
          !inputValue || inputValue.length === inputFormat.length
            ? format(date, inputFormat as DateFormat)
            : date;

        setFieldValue?.(
          sanitizedValue === '' || sanitizedValue == null
            ? null
            : sanitizedValue,
        );
      } catch {
        setFieldValue?.(inputValue === '' ? null : inputValue ?? null);
      }
    },
    [setFieldValue, onChange, inputFormat],
  );

  return (
    <DatePicker
      renderInput={({ inputProps = {}, value, ...params }) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          name={name}
          label={label}
          error={Boolean(error)}
          helperText={error}
          className={className}
          inputProps={{
            ...inputProps,
            onBlur: (...args) => {
              if (inputProps.onBlur) {
                inputProps.onBlur(...args);
              }
              onBlur?.(...args);
            },
          }}
        />
      )}
      value={pickerValue}
      inputFormat={inputFormat}
      mask={mask}
      views={DEFAULT_PICKER_VIEWS}
      onChange={onDateChange}
      minDate={minDate ? new Date(minDate) : undefined}
      maxDate={maxDate ? new Date(maxDate) : undefined}
      defaultCalendarMonth={
        defaultCalendarMonth ? new Date(defaultCalendarMonth) : undefined
      }
      disablePast={disablePast}
      disabled={disabled}
    />
  );
};
